/** Comentarios con menciones y archivos: la caja para escribirlos y el texto para leerlos.
 *
 * En el texto que viaja al servidor una mención es `<@id>` (como en Discord). Mientras se
 * escribe se ve y se edita como `@Nombre`: al elegir a alguien del desplegable se apunta quién
 * es, y al enviar se sustituye. Si alguien borra una letra de «@Nombre» la mención se rompe y
 * queda como texto normal, que es lo que se espera.
 */
import {
  useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode,
} from 'react'

import { ErrorDeApi } from '../api'
import type { Persona } from '../tipos'
import {
  BotonDeAdjuntar, PendientesDeSubir, manejadoresDeArchivos, useSubidas,
} from './adjuntos'

const ENLACE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]"'])/g
const MENCION = /<@(\d{15,20})>/g

/** El texto de un comentario con las personas mencionadas resaltadas y los enlaces pulsables. */
export function TextoConMenciones({
  cuerpo,
  menciones,
}: {
  cuerpo: string
  menciones: Record<string, string>
}) {
  const trozos: ReactNode[] = []
  let resto = 0
  const partes = cuerpo.split(MENCION)      // [texto, id, texto, id, …]
  partes.forEach((parte, i) => {
    if (i % 2 === 1) {
      trozos.push(
        <span key={`m${i}`} className="mencion">@{menciones[parte] ?? 'alguien'}</span>,
      )
      return
    }
    parte.split(ENLACE).forEach((seg, j) => {
      if (j % 2 === 1) {
        trozos.push(
          <a key={`e${i}-${j}-${resto++}`} href={seg} target="_blank" rel="noopener noreferrer">{seg}</a>,
        )
      } else if (seg) {
        trozos.push(seg)
      }
    })
  })
  return <div className="texto-largo">{trozos}</div>
}

/** De `<@id>` a `@Nombre`, para editar un comentario ya enviado. */
export function aEditable(cuerpo: string, menciones: Record<string, string>) {
  const mencionados: Record<string, string> = {}
  const texto = cuerpo.replace(MENCION, (_, id: string) => {
    const nombre = menciones[id]
    if (!nombre) return '@alguien'
    mencionados[nombre] = id
    return `@${nombre}`
  })
  return { texto, mencionados }
}

export interface DatosDeComentario {
  cuerpo: string
  adjuntos: string[]
  interno: boolean
}

interface Props {
  /** Dónde se suben los archivos: `${ruta}/archivos`. */
  rutaArchivos: string
  /** Campos extra de la subida: la solicitud o la página a la que pertenecen. */
  camposDeSubida?: Record<string, string>
  /** A quién se puede mencionar (distinto si el comentario es interno). */
  cargarCandidatos: (interno: boolean) => Promise<Persona[]>
  alEnviar: (datos: DatosDeComentario) => Promise<void>
  puedeInterno?: boolean
  placeholder?: string
  textoBoton?: string
  inicial?: { texto: string; mencionados: Record<string, string> }
  sinArchivos?: boolean
  alCancelar?: () => void
  autoFocus?: boolean
}

export function CajaDeComentario({
  rutaArchivos, camposDeSubida, cargarCandidatos, alEnviar, puedeInterno = false,
  placeholder = 'Escribe un comentario… usa @ para mencionar a alguien',
  textoBoton = 'Comentar', inicial, sinArchivos = false, alCancelar, autoFocus = false,
}: Props) {
  const [texto, setTexto] = useState(inicial?.texto ?? '')
  const [mencionados, setMencionados] = useState<Record<string, string>>(inicial?.mencionados ?? {})
  const [interno, setInterno] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const [candidatos, setCandidatos] = useState<Persona[] | null>(null)
  const [menu, setMenu] = useState<{ desde: number; consulta: string } | null>(null)
  const [indice, setIndice] = useState(0)
  const area = useRef<HTMLTextAreaElement>(null)
  const subidas = useSubidas(rutaArchivos, camposDeSubida)
  const arrastre = manejadoresDeArchivos(subidas)

  // Los candidatos se piden la primera vez que se enfoca la caja, y de nuevo si el comentario
  // pasa a ser interno (entonces hay menos gente a quien se pueda avisar).
  const cargados = useRef<boolean | null>(null)
  function asegurarCandidatos() {
    if (cargados.current === interno) return
    cargados.current = interno
    setCandidatos(null)
    cargarCandidatos(interno).then(setCandidatos).catch(() => setCandidatos([]))
  }
  useEffect(() => {
    if (cargados.current !== null) asegurarCandidatos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interno])

  const sugeridos = useMemo(() => {
    if (!menu || !candidatos) return []
    const q = menu.consulta.toLowerCase()
    return candidatos.filter((p) => p.nombre.toLowerCase().includes(q)).slice(0, 6)
  }, [menu, candidatos])

  function alCambiar(valor: string, cursor: number) {
    setTexto(valor)
    const antes = valor.slice(0, cursor)
    const m = /(^|[\s(])@([^\s@]{0,24})$/.exec(antes)
    if (m) {
      asegurarCandidatos()          // por si aún no se habían pedido (p. ej. sin pasar por el foco)
      setMenu({ desde: cursor - m[2].length - 1, consulta: m[2] })
      setIndice(0)
    } else {
      setMenu(null)
    }
  }

  function elegir(p: Persona) {
    if (!menu || !area.current) return
    const cursor = area.current.selectionStart
    const nuevo = `${texto.slice(0, menu.desde)}@${p.nombre} ${texto.slice(cursor)}`
    setTexto(nuevo)
    setMencionados((m) => ({ ...m, [p.nombre]: p.id }))
    setMenu(null)
    const pos = menu.desde + p.nombre.length + 2
    requestAnimationFrame(() => {
      area.current?.focus()
      area.current?.setSelectionRange(pos, pos)
    })
  }

  function alPulsar(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (menu && sugeridos.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndice((i) => (i + 1) % sugeridos.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIndice((i) => (i - 1 + sugeridos.length) % sugeridos.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); elegir(sugeridos[indice]); return }
    }
    if (menu && e.key === 'Escape') { e.preventDefault(); setMenu(null); return }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void enviar() }
  }

  /** De `@Nombre` a `<@id>`, empezando por los nombres más largos para que «Ana María» no se
   * confunda con «Ana». */
  function cuerpoParaEnviar(): string {
    let salida = texto
    for (const [nombre, id] of Object.entries(mencionados).sort((a, b) => b[0].length - a[0].length)) {
      salida = salida.split(`@${nombre}`).join(`<@${id}>`)
    }
    return salida.trim()
  }

  const vacio = !texto.trim() && subidas.tokens.length === 0
  async function enviar() {
    if (vacio || enviando || subidas.subiendo) return
    setEnviando(true)
    setError('')
    try {
      await alEnviar({ cuerpo: cuerpoParaEnviar(), adjuntos: subidas.tokens, interno })
      setTexto('')
      setMencionados({})
      setInterno(false)
      subidas.limpiar()
    } catch (e) {
      // Un 422 aquí suele ser «has mencionado a alguien que no puede ver esto»: se enseña tal cual.
      setError(e instanceof ErrorDeApi ? e.message : 'No se ha podido enviar.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="caja-comentario" {...(sinArchivos ? {} : arrastre)}>
      <div className="caja-comentario__area">
        <textarea
          ref={area}
          value={texto}
          autoFocus={autoFocus}
          placeholder={placeholder}
          aria-label="Comentario"
          rows={2}
          maxLength={10000}
          onFocus={asegurarCandidatos}
          onChange={(e) => alCambiar(e.target.value, e.target.selectionStart)}
          onKeyDown={alPulsar}
        />
        {menu && (
          <div className="menciones-lista" role="listbox" aria-label="Personas">
            {candidatos === null && <div className="menciones-lista__vacio">Cargando…</div>}
            {candidatos !== null && sugeridos.length === 0 && (
              <div className="menciones-lista__vacio">
                {candidatos.length === 0
                  ? 'Nadie a quien mencionar aquí.'
                  : 'Nadie coincide. Solo se puede mencionar a quien puede ver esto.'}
              </div>
            )}
            {sugeridos.map((p, i) => (
              <button
                key={p.id} type="button" role="option" aria-selected={i === indice}
                className={`menciones-lista__fila${i === indice ? ' menciones-lista__fila--activa' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); elegir(p) }}
                onMouseEnter={() => setIndice(i)}
              >
                <span className="persona__inicial" aria-hidden="true">{p.nombre.trim().charAt(0).toUpperCase()}</span>
                {p.nombre}
              </button>
            ))}
          </div>
        )}
      </div>

      {!sinArchivos && <PendientesDeSubir subidas={subidas} />}
      {error && <div className="error" role="alert" style={{ marginTop: 8 }}>{error}</div>}

      <div className="caja-comentario__pie">
        {!sinArchivos && <BotonDeAdjuntar subidas={subidas} />}
        {puedeInterno && (
          <label className="caja-comentario__interno">
            <input type="checkbox" checked={interno} onChange={(e) => setInterno(e.target.checked)} />
            Interno (no lo ve quien lo pidió)
          </label>
        )}
        <span className="caja-comentario__ayuda">Ctrl + Intro para enviar</span>
        {alCancelar && <button type="button" className="boton boton--plano" onClick={alCancelar}>Cancelar</button>}
        <button
          type="button" className="boton boton--principal" onClick={() => void enviar()}
          disabled={enviando || vacio || subidas.subiendo}
        >
          {enviando ? 'Enviando…' : subidas.subiendo ? 'Subiendo…' : textoBoton}
        </button>
      </div>
    </div>
  )
}
